import { ChatBotHistory, ChatBotToolRecord } from "@/API";
import { TimestampToDate } from "@/Helpers/DateHelper";
import { MarkdownMessage } from "@/Components/MarkdownMessage";
import { ChatBotRecordRoleToNumber } from "./ChatBotRecordRoleToNumber";
import {
    ActionIcon,
    Badge,
    Box,
    CopyButton,
    Divider,
    Group,
    Paper,
    Stack,
    Text,
    ThemeIcon,
    Tooltip,
} from "@mantine/core";
import {
    IconCopy,
    IconMessage,
    IconThumbDown,
    IconThumbUp,
} from "@tabler/icons-react";

export interface ChatBotHistoryItemProps {
    history: ChatBotHistory;
    historyStartTimestamp?: number;
    enableMarkdown?: boolean;
}

export function ChatBotHistoryItem({
    history,
    historyStartTimestamp = 0,
    enableMarkdown = true,
}: ChatBotHistoryItemProps) {
    const allRecords = (history.records ?? [])
        .concat(history.toolRecords ?? [])
        .filter((x) => !!x);

    allRecords.sort((a, b) => {
        const t1 = a?.timestamp ?? 0;
        const t2 = b?.timestamp ?? 0;
        if (t1 !== t2) return t1 - t2;
        return ChatBotRecordRoleToNumber(a?.role) - ChatBotRecordRoleToNumber(b?.role);
    });

    const clientInfo = history.clientInfo;
    const historyCreatedTime = history.createdTimestamp ?? 0;
    const isClearedBefore =
        historyStartTimestamp > 0 &&
        historyCreatedTime > 0 &&
        historyCreatedTime <= historyStartTimestamp;
    const feedback = history.feedback;
    const isGood = feedback?.rating === "Good";
    const isBad = feedback?.rating === "Bad";
    const hasRatingBadge = isGood || isBad;
    const hasCommentFeedback = !!feedback?.comment;
    const hasTags = !!(history.tags && history.tags.length > 0);

    return (
        <Box opacity={isClearedBefore ? 0.75 : 1}>
            <Tooltip
                position="top"
                multiline
                events={{ hover: true, focus: false, touch: true }}
                style={{ whiteSpace: "pre-line" }}
                disabled={!history.tokenUsage?.length}
                label={history.tokenUsage
                    ?.map(
                        (x) =>
                            `model:${x.model}\nInputTokens:${x.inputTokens}\nOutputTokens:${x.outputTokens}`
                    )
                    .join("\n")}
            >
                <Divider
                    my="md"
                    label={TimestampToDate(history.createdTimestamp ?? 0).toLocaleString()}
                />
            </Tooltip>

            {(isClearedBefore || hasTags || hasRatingBadge) && (
                <Group mb="sm" gap="xs">
                    {isClearedBefore && (
                        <Badge color="red" variant="light">
                            ⚠️ 清空時段前的歷史紀錄 (使用者不可見)
                        </Badge>
                    )}
                    {history.tags?.map((tag, tagIdx) => (
                        <Badge key={tagIdx} color="orange">
                            {tag}
                        </Badge>
                    ))}
                    {isGood && (
                        <Badge
                            color="green"
                            variant="light"
                            leftSection={<IconThumbUp size={12} />}
                        >
                            正向回饋
                        </Badge>
                    )}
                    {isBad && (
                        <Badge
                            color="red"
                            variant="light"
                            leftSection={<IconThumbDown size={12} />}
                        >
                            負向回饋
                        </Badge>
                    )}
                </Group>
            )}

            {allRecords.map((d, recordIdx) => {
                const isFirstRecord = recordIdx === 0;
                return (
                    <Group
                        key={recordIdx}
                        mb="md"
                        pos="relative"
                        justify={d.role === "User" ? "start" : "end"}
                    >
                        <Paper
                            p="sm"
                            radius="md"
                            maw="75%"
                            bg={
                                d.role === "User"
                                    ? "var(--mantine-primary-color-filled)"
                                    : d.role === "Tool"
                                    ? "green"
                                    : "gray"
                            }
                        >
                            <Group gap={5} align="stretch">
                                <Box
                                    c="white"
                                    style={{
                                        fontSize: "var(--mantine-font-size-sm)",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {d.role === "Tool" && (
                                        <>
                                            工具:{(d as ChatBotToolRecord).pluginName}.
                                            {(d as ChatBotToolRecord).functionName}
                                            <br />
                                            參數:{(d as ChatBotToolRecord).arguments}
                                            <br />
                                        </>
                                    )}
                                    {!enableMarkdown || d.role === "Tool" ? (
                                        <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                                            {d.message}
                                        </Text>
                                    ) : (
                                        <MarkdownMessage>{d.message}</MarkdownMessage>
                                    )}
                                </Box>
                                <Stack justify="end">
                                    <CopyButton value={d.message ?? ""}>
                                        {({ copied, copy }) => (
                                            <Tooltip
                                                label={copied ? "已複製" : ""}
                                                disabled={!copied}
                                                withArrow
                                            >
                                                <ActionIcon
                                                    color="white"
                                                    size={20}
                                                    variant="transparent"
                                                    onClick={copy}
                                                >
                                                    <IconCopy />
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </CopyButton>
                                </Stack>
                            </Group>
                            {isFirstRecord && clientInfo && (clientInfo.ip || clientInfo.userAgent) && (
                                <Group
                                    gap="xs"
                                    mt="xs"
                                    pt="xs"
                                    style={{ borderTop: "1px solid rgba(255, 255, 255, 0.25)" }}
                                >
                                    {clientInfo.ip && (
                                        <CopyButton value={clientInfo.ip}>
                                            {({ copied, copy }) => (
                                                <Tooltip
                                                    label={copied ? "已複製 IP" : clientInfo.ip}
                                                    withArrow
                                                >
                                                    <Badge
                                                        size="xs"
                                                        color="white"
                                                        variant="outline"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            copy();
                                                        }}
                                                    >
                                                        IP: {clientInfo.ip}
                                                    </Badge>
                                                </Tooltip>
                                            )}
                                        </CopyButton>
                                    )}
                                    {clientInfo.userAgent && (
                                        <CopyButton value={clientInfo.userAgent}>
                                            {({ copied, copy }) => (
                                                <Tooltip
                                                    label={copied ? "已複製 User-Agent" : clientInfo.userAgent}
                                                    multiline
                                                    w={350}
                                                    withArrow
                                                >
                                                    <Badge
                                                        size="xs"
                                                        color="white"
                                                        variant="outline"
                                                        maw={220}
                                                        style={{
                                                            cursor: "pointer",
                                                            textOverflow: "ellipsis",
                                                            overflow: "hidden",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            copy();
                                                        }}
                                                    >
                                                        {clientInfo.userAgent}
                                                    </Badge>
                                                </Tooltip>
                                            )}
                                        </CopyButton>
                                    )}
                                </Group>
                            )}
                        </Paper>
                    </Group>
                );
            })}

            {hasCommentFeedback && feedback && (
                <Group justify="end" mb="md">
                    <Paper p="xs" px="sm" radius="md" withBorder maw="75%" bg="var(--mantine-color-body)">
                        <Group gap="xs" align="center">
                            <ThemeIcon
                                color={isGood ? "green" : isBad ? "red" : "blue"}
                                variant="light"
                                size="sm"
                            >
                                {isGood ? (
                                    <IconThumbUp size={14} />
                                ) : isBad ? (
                                    <IconThumbDown size={14} />
                                ) : (
                                    <IconMessage size={14} />
                                )}
                            </ThemeIcon>
                            <Text size="xs" fw={600} c={isGood ? "green" : isBad ? "red" : "blue"}>
                                {isGood ? "正向回饋" : isBad ? "負向回饋" : "使用者回饋"}
                            </Text>
                            {feedback.timestamp && feedback.timestamp > 0 && (
                                <Text size="xs" c="dimmed">
                                    {TimestampToDate(feedback.timestamp).toLocaleString()}
                                </Text>
                            )}
                        </Group>
                        {feedback.comment && (
                            <Text
                                size="xs"
                                mt={4}
                                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                            >
                                {feedback.comment}
                            </Text>
                        )}
                    </Paper>
                </Group>
            )}
        </Box>
    );
}
